/** The two facts a turn's decisions all depend on: who carries the army, and
 * which of our towns is about to be attacked.
 *
 * Both are cheap and both are needed by the map planner and the town planner at
 * once, so they live here rather than in either. `town.ts` already imports from
 * `adventure.ts`; putting these there and importing them back would close a
 * cycle for no gain.
 *
 * Everything is read out of the shadow, so I9 is inherited rather than
 * re-argued: a threatened town is a town with an enemy hero the observation is
 * lighting up right now, and a hero the fog is hiding threatens nothing the AI
 * is allowed to know about.
 */

import { tileDistance } from "@game/core";
import type { CaravanState, HeroId, TownId, TownState } from "@game/core";
import { entityKeys } from "@game/core";
import type { Shadow } from "./shadow.js";
import type { DifficultyProfile } from "./difficulty.js";
import { TIER_WEIGHT_BP_PER_TIER, armyStrength } from "./evaluate.js";

/**
 * The hero the kingdom's war is fought by: the one carrying the most army.
 *
 * "Force concentration" is one line of policy and this is it - every rule that
 * asks "should this hero march" or "should this hero be handed the garrison"
 * asks about the same hero, so an army that is being gathered goes to one place
 * instead of being split evenly between four scouts.
 */
export function mainHero(sh: Shadow): HeroId | null {
  let best: HeroId | null = null;
  let bestValue = -1;
  for (const h of sh.obs.heroes) {
    if (h.owner !== sh.player || h.detail === null) continue;
    const v = armyStrength(sh.content, h.army);
    // Ties break on id so two heroes with empty armies do not swap the title
    // between commands and undo each other's transfers.
    if (v > bestValue || (v === bestValue && best !== null && h.id < best)) {
      bestValue = v;
      best = h.id;
    }
  }
  return best;
}

/** Everything the AI could put in a fight today, its garrisons included. */
export function fieldAndGarrison(sh: Shadow): number {
  let v = 0;
  for (const h of sh.obs.heroes) {
    if (h.owner !== sh.player) continue;
    v += armyStrength(sh.content, h.army);
  }
  for (const t of sh.obs.towns) {
    if (t.detail === null) continue;
    v += armyStrength(sh.content, t.garrison);
  }
  return v;
}

export interface TownThreat {
  readonly town: TownId;
  /** Closest enemy hero, in tiles. */
  readonly distance: number;
  /** True when nothing of ours is standing in the town to meet him. */
  readonly undefended: boolean;
  /** What the town is worth losing, in the same gold-equivalents as everything. */
  readonly stake: number;
  /**
   * Army already on the road to this town that lands before the enemy can,
   * in the same units as `armyStrength`. Zero for every town with nothing
   * inbound, which is most of them.
   *
   * A hall with a column arriving tomorrow is not the same emergency as a hall
   * with nothing coming, and until the observation carried caravans the planner
   * could not tell the two apart - so it recalled a hero from a siege to defend
   * a town that was about to defend itself.
   */
  readonly relief: number;
}

/**
 * Tiles a hero covers in a day, for turning "eight tiles away" into "three days
 * away". Deliberately pessimistic: a fast hero on a road does much better, and
 * counting relief that arrives *after* the attack is the expensive mistake.
 */
export const THREAT_TILES_PER_DAY = 8;

/** A town with a fort is worth defending; one without is worth defending more,
 * because it has no walls to do the job. */
export const THREAT_STAKE_BASE = 26000;
export const THREAT_STAKE_PER_BUILDING = 1200;

/**
 * Our towns with an enemy hero bearing down on them, nearest first.
 *
 * `defendRadius` is a difficulty knob rather than a constant: seeing a threat
 * ten tiles out and turning for home is *playing better*, and an easy computer
 * that only notices an enemy four tiles away loses towns it could have held.
 */
export function threatenedTowns(sh: Shadow, p: DifficultyProfile): readonly TownThreat[] {
  const out: TownThreat[] = [];
  const inbound = caravansByTown(sh);
  for (const t of sh.obs.towns) {
    const d: TownState | null = t.detail;
    if (d === null) continue;
    let nearest = -1;
    for (const e of sh.enemies) {
      if (e.kind !== "hero" || !e.fresh || e.z !== t.z) continue;
      if (e.owner === sh.player) continue;
      const dist = tileDistance(e.x, e.y, t.x, t.y);
      if (dist > p.defendRadius) continue;
      if (nearest < 0 || dist < nearest) nearest = dist;
    }
    if (nearest < 0) continue;
    // The enemy needs at least this many days to walk the last stretch; a
    // column landing inside that window fights in the battle, one landing after
    // it arrives at a hall that has already changed hands.
    const daysOfGrace = Math.max(1, Math.ceil(nearest / THREAT_TILES_PER_DAY));
    out.push({
      town: d.id,
      distance: nearest,
      undefended: d.visitingHero === null,
      stake: THREAT_STAKE_BASE + d.built.length * THREAT_STAKE_PER_BUILDING,
      relief: reliefFor(sh, inbound.get(d.id as string), sh.state.turn.day + daysOfGrace),
    });
  }
  out.sort((a, b) => (a.distance - b.distance) || (a.town < b.town ? -1 : a.town > b.town ? 1 : 0));
  return out;
}

/** The threatened-town ids, for the callers that only need the question asked. */
export function threatenedTownIds(sh: Shadow, p: DifficultyProfile): ReadonlySet<string> {
  const out = new Set<string>();
  for (const t of threatenedTowns(sh, p)) out.add(t.town as string);
  return out;
}

/**
 * The viewer's own columns, indexed by the town they are walking to.
 *
 * Built once per `threatenedTowns` call rather than scanned per town: the map
 * has one caravan list and up to a dozen towns, and the quadratic version of
 * this showed up in nothing but was pointless.
 */
export function caravansByTown(sh: Shadow): ReadonlyMap<string, CaravanState[]> {
  const out = new Map<string, CaravanState[]>();
  for (const id of entityKeys(sh.state.caravans)) {
    const cv = sh.state.caravans[id];
    if (cv === undefined || cv.owner !== sh.player) continue;
    const key = cv.to as string;
    const list = out.get(key);
    if (list === undefined) out.set(key, [cv]);
    else list.push(cv);
  }
  return out;
}

/** What lands on or before `byDay`, in `armyStrength` units. */
function reliefFor(
  sh: Shadow, columns: readonly CaravanState[] | undefined, byDay: number,
): number {
  if (columns === undefined) return 0;
  let v = 0;
  for (const cv of columns) {
    if (cv.arrivesOnDay > byDay) continue;
    const def = sh.content.creatures.get(cv.def);
    if (def === undefined) continue;
    v += Math.floor((def.aiValue * cv.count * (10000 + TIER_WEIGHT_BP_PER_TIER * def.tier)) / 10000);
  }
  return v;
}
